import React, { useEffect, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Box, Stack, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useTableScroll } from '../../../hooks/useTableScroll';
import CustomSelect from '../../../components/inputs/select/CustomSelect';
import NotChecked from '../../../assets/iconsSmall/check_not_selected.png';
import Checked from '../../../assets/iconsSmall/check_selected_mobile.png';
import Exclamation from '../../../assets/iconsSmall/exclamation_mark_dark.svg';
import { isMobileOnly } from 'react-device-detect';
import { showToast } from '../../../config/helpers';
import ModalSelectPaymentDate from './ModalSelectPaymentDate';
import ModalChargeConfirm from './ModalChargeConfirm';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

function PaymentDashboard({
    tableDetails,
    setTableDetails,
    dashboardMode = 'policies', // 'policies' or 'invoices'
    currentPage,
    setCurrentPage,
    loadedPages,
    getDashboardData,
    onCheckInvoices,
    currentPolicyFilter = null, // Track the current policy being filtered
    onAllocate,
    onCharge,
    onChargeSelected,
    onChargeComplete,
    onPolicySearch,
    currentPolicySearch = null, // Current search term
    onPaymentDateUpdate, // New prop for handling payment date updates
}) {
    const { t } = useTranslation();
    const { bodyRef, headerRef, syncScroll, listOfItemsRef } = useTableScroll();

    const [allSelected, setAllSelected] = useState(false);
    const [filteredData, setFilteredData] = useState([]);

    // Filter states
    const [policyNumberFilter, setPolicyNumberFilter] = useState(null);
    const [invoiceNumberFilter, setInvoiceNumberFilter] = useState(null);
    const [customerNameFilter, setCustomerNameFilter] = useState(null);
    const [statusFilter, setStatusFilter] = useState(null);
    
    // Policy search value for CreatableSelect display
    const [searchInputValue, setSearchInputValue] = useState('');
    const [searchFieldValue, setSearchFieldValue] = useState(null); // Track the displayed value in search field
    
    // Modal state for payment date selection
    const [openPaymentDateModal, setOpenPaymentDateModal] = useState(false);
    const [selectedInvoiceForDate, setSelectedInvoiceForDate] = useState(null);

    // Modal state for charge confirmation
    const [chargeModalOpen, setChargeModalOpen] = useState(false);
    const [chargeModalInvoice, setChargeModalInvoice] = useState(null);
    const [chargeModalActionType, setChargeModalActionType] = useState('charge');
    
    // Clear search input when policy search changes externally
    useEffect(() => {
        if (!currentPolicySearch) {
            setSearchInputValue('');
            setSearchFieldValue(null);
        } else {
            // If there's an active policy search, show it in the search field
            setSearchFieldValue({ label: currentPolicySearch, value: currentPolicySearch });
        }
    }, [currentPolicySearch]);

    // Clear API search when local policy filter is applied
    useEffect(() => {
        if (policyNumberFilter && currentPolicySearch && onPolicySearch) {
            // If user applies local filter while there's an API search active, clear the API search
            onPolicySearch(null);
        }
    }, [policyNumberFilter, currentPolicySearch, onPolicySearch]);

    // Update search field display when policy filter changes
    useEffect(() => {
        if (policyNumberFilter) {
            setSearchFieldValue(policyNumberFilter);
        } else if (currentPolicySearch) {
            // If there's an active API search, show it in the search field
            setSearchFieldValue({ label: currentPolicySearch, value: currentPolicySearch });
        } else {
            setSearchFieldValue(null);
        }
    }, [policyNumberFilter, currentPolicySearch]);

    // Pagination handlers
    const handleNextPage = () => {
        if (dashboardMode === 'invoices') {
            // Invoices don't have pagination
            return;
        }
        const nextPage = currentPage + 1;
        setCurrentPage(nextPage);
        getDashboardData(nextPage, dashboardMode, currentPolicyFilter);
    };

    const handlePrevPage = () => {
        if (dashboardMode === 'invoices') {
            // Invoices don't have pagination
            return;
        }
        const prevPage = currentPage - 1;
        if (prevPage >= 1) {
            setCurrentPage(prevPage);
            getDashboardData(prevPage, dashboardMode, currentPolicyFilter);
        }
    };

    const hasNextPageHandler = () => {
        if (dashboardMode === 'invoices') {
            // Invoices don't have pagination
            return false;
        }
        const cacheKey = currentPage + 1;
        const hasPage = loadedPages.has(cacheKey);
        const currentData = loadedPages.get(currentPage);
        return hasPage || currentData?.hasNextPage;
    };

    const disableNextButton = dashboardMode === 'invoices' || !hasNextPageHandler();
    const disablePrevButton = dashboardMode === 'invoices' || currentPage <= 1;

    // Define headers for different dashboard modes
    const policiesHeaders = [
        { title: 'policyNumber' },
        { title: 'customerName' },
        { title: 'status' },
        { title: 'dateIssue' },
        { title: 'action' },
    ];

    const invoicesHeaders = [
        { title: 'policyNumber' },
        { title: 'invoiceNumber' },
        { title: 'paymentPeriod' },
        { title: 'paymentAmount' },
        { title: 'paymentStatus' },
        { title: 'paymentDate' },
        { title: 'stripeId' },
        { title: 'paymentSource' },
        { title: 'paymentMethod' },
        { title: 'paymentMethodDetails' },
        { title: 'action' },
    ];

    const currentHeaders = dashboardMode === 'policies' ? policiesHeaders : invoicesHeaders;

    // Generate filter options from data
    const getPolicyNumberOptions = () => {
        const uniqueValues = [...new Set(tableDetails.map(item =>
            dashboardMode === 'policies' ? item.policy_number : item.policy_no
        ).filter(Boolean))];
        return uniqueValues.map(value => ({ label: value, value }));
    };

    const getInvoiceNumberOptions = () => {
        if (dashboardMode === 'invoices') {
            const uniqueValues = [...new Set(tableDetails.map(item => item.invoice_number).filter(Boolean))];
            return uniqueValues.map(value => ({ label: value, value }));
        }
        // For policies mode, we don't have invoice numbers, so return empty array
        return [];
    };

    const getCustomerNameOptions = () => {
        const uniqueValues = [...new Set(tableDetails.map(item => item.customer_name).filter(Boolean))];
        return uniqueValues.map(value => ({ label: value, value }));
    };

    const getStatusOptions = () => {
        const uniqueValues = [...new Set(tableDetails.map(item =>
            dashboardMode === 'policies' ? item.status : item.payment_status
        ).filter(Boolean))];
        return uniqueValues.map(value => ({ label: value, value }));
    };

    // Handle policy number search - only for new entries not in current data
    const handlePolicyNumberSearch = (inputValue) => {
        if (dashboardMode === 'policies' && onPolicySearch) {
            // Clear local policy filter when performing API search
            if (inputValue && policyNumberFilter) {
                setPolicyNumberFilter(null);
            }
            // Update search field display
            setSearchFieldValue(inputValue ? { label: inputValue.value, value: inputValue.value } : null);
            // Reset to first page and trigger search
            setCurrentPage(1);
            onPolicySearch(inputValue?.value || null);
        }
    };

    // Handle local filtering when selecting existing policy from dropdown
    const handleLocalPolicyFilter = (selectedOption) => {
        // This will trigger local filtering through the existing filter logic
        setPolicyNumberFilter(selectedOption);
        // Update search field display
        setSearchFieldValue(selectedOption);
    };

    // Apply filters to data
    useEffect(() => {
        let filtered = [...tableDetails];

        // Apply policy number filter for both modes
        if (policyNumberFilter) {
            filtered = filtered.filter(item => {
                const fieldValue = dashboardMode === 'policies' ? item.policy_number : item.policy_no;
                return fieldValue === policyNumberFilter.value;
            });
        }

        if (invoiceNumberFilter) {
            filtered = filtered.filter(item => {
                if (dashboardMode === 'invoices') {
                    return item.invoice_number === invoiceNumberFilter.value;
                }
                // For policies mode, invoice number filter doesn't apply
                return true;
            });
        }

        if (customerNameFilter) {
            filtered = filtered.filter(item => {
                return item.customer_name === customerNameFilter.value;
            });
        }

        if (statusFilter) {
            filtered = filtered.filter(item => {
                const fieldValue = dashboardMode === 'policies' ? item.status : item.payment_status;
                return fieldValue === statusFilter.value;
            });
        }

        setFilteredData(filtered);
    }, [tableDetails, policyNumberFilter, invoiceNumberFilter, customerNameFilter, statusFilter, dashboardMode]);

    const rowClickHandler = (index) => {
        // Find the original index in tableDetails
        const originalItem = filteredData[index];
        let originalIndex = -1;

        if (dashboardMode === 'invoices') {
            // For invoices, use invoice_no as the unique identifier
            originalIndex = tableDetails.findIndex(item => item.invoice_number === originalItem.invoice_number);
            
            // Prevent selection of PAID invoices
            if (originalItem.payment_status === 'PAID' && originalItem.paid === true) {
                showToast(t('invoiceAlreadyPaid'), { error: true });
                return;
            }
            
            // A MISSING PAYMENT DATE NO LONGER BLOCKS SELECTION.
            //
            // The date is what Insly books an allocation on, so allocating without one is
            // impossible — but charging does not use it at all: a charge raises a new
            // Stripe invoice and the date is whenever the customer pays. Refusing the
            // checkbox made every unpaid instalment unselectable for charging, which is
            // precisely the set an admin wants to charge.
            //
            // The requirement moved to the ALLOCATE action, which names the offending
            // invoices instead of silently doing nothing.
        } else {
            // For policies, use id as the unique identifier
            originalIndex = tableDetails.findIndex(item => item.id === originalItem.id);
        }

        if (originalIndex !== -1) {
            setTableDetails((prevState) => {
                const updatedData = [...prevState];
                updatedData[originalIndex] = {
                    ...updatedData[originalIndex],
                    selected: !updatedData[originalIndex].selected,
                };
                return updatedData;
            });
        }
    };

    const selectAllHandler = () => {
        // Update all visible filtered items
        let filteredIdentifiers;

        if (dashboardMode === 'invoices') {
            // Only genuinely finished rows are excluded — paid AND allocated. A missing
            // payment date is an allocate-time problem, not a selection one.
            filteredIdentifiers = filteredData
                .filter(item => !(item.payment_status === 'PAID' && item.paid === true))
                .map(item => item.invoice_number);
        } else {
            filteredIdentifiers = filteredData.map(item => item.id);
        }

        setTableDetails((prevState) => {
            const updatedData = prevState.map((obj) => {
                const identifier = dashboardMode === 'invoices' ? obj.invoice_number : obj.id;
                if (filteredIdentifiers.includes(identifier)) {
                    return { ...obj, selected: !allSelected };
                }
                return obj;
            });
            return updatedData;
        });
        setAllSelected(!allSelected);
    };

    useEffect(() => {
        let selectableItems;
        
        if (dashboardMode === 'invoices') {
            // Mirrors selectAllHandler exactly — if the two disagree, the header tick
            // never lights up even when everything selectable is selected.
            selectableItems = filteredData.filter(obj => !(obj.payment_status === 'PAID' && obj.paid === true));
        } else {
            selectableItems = filteredData;
        }
        
        const allFilteredSelected =
            selectableItems?.length > 0 &&
            selectableItems.every((obj) => obj.selected === true);

        setAllSelected(allFilteredSelected);
    }, [filteredData, dashboardMode]);

    /**
     * Allocation needs a payment date on every selected row; charging needs none.
     *
     * Checked here rather than at selection time so the admin finds out which invoices
     * are missing a date, by number, instead of discovering that a checkbox will not
     * tick and having to guess why.
     */
    const handleAllocateClick = () => {
        const missing = (filteredData || [])
            .filter((invoice) => invoice.selected && !invoice.payment_date)
            .map((invoice) => invoice.invoice_number);

        if (missing.length > 0) {
            showToast(
                `${t('pleaseSelectPaymentDate')}: ${missing.join(', ')}`,
                { error: true },
            );
            return;
        }

        onAllocate();
    };

    // Check if any invoices are selected for the Allocate button
    const hasSelectedInvoices = () => {
        if (dashboardMode !== 'invoices') return false;
        return filteredData?.some((invoice) => invoice.selected === true) || false;
    };

    const renderPolicyRow = (row, index) => {
        return (
            <TableRow
                key={`row-${index}`}
                className={`reset-table-new__body`}
            >
                {/* <TableCell key={0} className="cell-transparent">
                    <div
                        style={{
                            alignItems: 'flex-start',
                            padding: '5px',
                        }}
                    >
                        <img
                            src={row.selected ? Checked : NotChecked}
                            alt="item-check-box"
                        />
                    </div>
                </TableCell> */}
                <TableCell key={1}>
                    <div>
                        <span>
                            {row.policy_number || t('noInfo')}
                        </span>
                    </div>
                </TableCell>
                <TableCell key={2}>
                    <div>
                        <span>
                            {row.customer_name || t('noInfo')}
                        </span>
                    </div>
                </TableCell>
                <TableCell key={3}>
                    <div>
                        <span>
                            {row.status || t('noInfo')}
                        </span>
                    </div>
                </TableCell>
                <TableCell key={4} sx={{ padding: '0 5px' }}>
                    <div>
                        <span>
                            {row.date_issue || t('noInfo')}
                        </span>
                    </div>
                </TableCell>
                <TableCell key={5} sx={{ padding: '0 5px' }}>
                    <div className="d-flex flex-row justify-content-center">
                        <button
                            className="button_red_text"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (onCheckInvoices) {
                                    onCheckInvoices(row.policy_id);
                                }
                            }}
                            style={{ fontSize: '12px', padding: '4px 8px', marginRight: '8px' }}
                        >
                            {t('checkInvoices')}
                        </button>
                    </div>
                </TableCell>
            </TableRow>
        );
    };

    const handlePaymentDateSelect = (formattedDate, invoiceData) => {
        if (onPaymentDateUpdate) {
            onPaymentDateUpdate(formattedDate, invoiceData);
        }
    };

    const renderInvoiceRow = (row, index) => (
        <TableRow
            key={`row-${index}`}
            className={`reset-table-new__body ${row.selected ? 'edit-unselected' : ''} edit-rows`}
            onClick={() => rowClickHandler(index)}
        >
            <TableCell key={0} className="cell-transparent">
                <div
                    style={{
                        alignItems: 'flex-start',
                        padding: '5px',
                    }}
                >
                    <img
                        src={row.selected ? Checked : NotChecked}
                        alt="item-check-box"
                    />
                </div>
            </TableCell>
            <TableCell key={1}>
                <div>
                    <span>
                        {row.policy_no || t('noInfo')}
                    </span>
                </div>
            </TableCell>
            <TableCell key={2}>
                <div>
                    <span>
                        {row.invoice_number || t('noInfo')}
                    </span>
                </div>
            </TableCell>
            <TableCell key={3}>
                <div>
                    <span>
                        {row.payment_period || t('noInfo')}
                    </span>
                </div>
            </TableCell>
            <TableCell key={4}>
                <div>
                    <span>
                        {row.payment_amount ? `${row.payment_amount} €` : t('noInfo')}
                    </span>
                </div>
            </TableCell>
            <TableCell key={5}>
                <div>
                    <span>
                        {
                            row.payment_status === 'PAID'
                                ? (row.paid === true
                                    ? 'PAID (Allocated)'
                                    : 'PAID (Not allocated)')
                                : (row.payment_status ? row.payment_status : t('noInfo'))
                        }
                    </span>
                </div>
            </TableCell>
            <TableCell key={6} sx={{ padding: '0 5px' }}>
                <div className="d-flex flex-row justify-content-center align-items-center" style={{ gap: '5px' }}>
                    <span>
                        {row.payment_date}
                    </span>
                    {(row.had_no_payment_date || !row.payment_date) && (
                        <div
                            className="back_icon_red"
                            style={{ width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                            <EditOutlinedIcon
                                sx={{
                                    color: '#FFFAF0',
                                    fontSize: '18px',
                                    cursor: 'pointer',
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedInvoiceForDate(row);
                                    setOpenPaymentDateModal(true);
                                }}
                            />
                        </div>
                    )}
                </div>
            </TableCell>
            <TableCell key={7}>
                <div>
                    <span>
                        {row.stripe_id || t('noInfo')}
                    </span>
                </div>
            </TableCell>
            <TableCell key={8}>
                <div>
                    <span>
                        {row.payment_source || t('noInfo')}
                    </span>
                </div>
            </TableCell>
            <TableCell key={9}>
                <div>
                    <span>
                        {row.payment_method || t('noInfo')}
                    </span>
                </div>
            </TableCell>
            <TableCell key={10}>
                <div>
                    <span>
                        {row.payment_method_details || t('noInfo')}
                    </span>
                </div>
            </TableCell>
            <TableCell key={11} sx={{ padding: '0 5px' }}>
                <div className="d-flex flex-row justify-content-center" style={{ gap: '4px' }}>
                    <button
                        className="button_red_text"
                        disabled={row.payment_status === 'PAID' || parseFloat(row.payment_amount) < 0.5}
                        onClick={(e) => {
                            e.stopPropagation();
                            setChargeModalInvoice(row);
                            setChargeModalActionType('charge');
                            setChargeModalOpen(true);
                        }}
                        style={{ fontSize: '12px', padding: '4px 8px' }}
                    >
                        {t('charge')}
                    </button>
                    <button
                        className="button_red_text"
                        disabled={row.payment_status === 'PAID' || parseFloat(row.payment_amount) < 0.5}
                        onClick={(e) => {
                            e.stopPropagation();
                            setChargeModalInvoice(row);
                            setChargeModalActionType('sendInvoice');
                            setChargeModalOpen(true);
                        }}
                        style={{ fontSize: '12px', padding: '4px 8px' }}
                    >
                        {t('sendInvoice')}
                    </button>
                </div>
            </TableCell>
        </TableRow>
    );

    return (
        <>
            {/* Payment Date Selection Modal */}
            <ModalSelectPaymentDate
                openModal={openPaymentDateModal}
                setOpenModal={setOpenPaymentDateModal}
                onDateSelect={handlePaymentDateSelect}
                invoiceData={selectedInvoiceForDate}
            />

            {/* Charge Confirmation Modal */}
            <ModalChargeConfirm
                openModal={chargeModalOpen}
                setOpenModal={setChargeModalOpen}
                invoiceData={chargeModalInvoice}
                actionType={chargeModalActionType}
                onConfirm={onCharge}
                onComplete={onChargeComplete}
            />
            
            {/* Filters Row */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '10px',
                    position: 'relative',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        gap: '7px',
                        margin: '16px',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                    }}
                >
                    {/* Policy Search Field - Always show for policies mode */}
                    {dashboardMode === 'policies' && (
                        <div style={{ minWidth: '275px' }}>
                            <CustomSelect
                                options={getPolicyNumberOptions()}
                                placeholder={t('policyNo')}
                                value={searchFieldValue}
                                inputValue={searchInputValue}
                                onInputChange={(value) => setSearchInputValue(value)}
                                onCreateOption={(inputValue) => {
                                    // When user creates a new option (policy not in current data), trigger API search
                                    handlePolicyNumberSearch({ value: inputValue });
                                    setSearchInputValue(''); // Clear input after search
                                }}
                                onChange={(newValue) => {
                                    // When user selects from existing options, just filter locally
                                    if (newValue) {
                                        // Check if the selected policy is the same as the current API search
                                        if (currentPolicySearch && newValue.value === currentPolicySearch) {
                                            // User clicked on the same policy that was fetched via API search - do nothing
                                            setSearchInputValue(''); // Just clear input
                                            return;
                                        }
                                        
                                        // Check if this is an existing policy in current data
                                        const existingPolicyOptions = getPolicyNumberOptions();
                                        const isExistingPolicy = existingPolicyOptions.some(option => option.value === newValue.value);
                                        
                                        if (isExistingPolicy) {
                                            // Policy exists in current data - apply local filter only
                                            handleLocalPolicyFilter(newValue);
                                            setSearchInputValue(''); // Clear input after selection
                                        } else {
                                            // Policy doesn't exist in current data - trigger API search
                                            handlePolicyNumberSearch(newValue);
                                            setSearchInputValue(''); // Clear input after search
                                        }
                                    } else {
                                        // Handle clear - clear both local filter and API search without triggering new API call
                                        setPolicyNumberFilter(null);
                                        setSearchFieldValue(null);
                                        setSearchInputValue('');
                                        // Only clear API search if there was one active
                                        if (currentPolicySearch && onPolicySearch) {
                                            onPolicySearch(null);
                                        }
                                    }
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && searchInputValue && !e.defaultPrevented) {
                                        // Check if the typed value is the same as current API search
                                        if (currentPolicySearch && searchInputValue.toLowerCase() === currentPolicySearch.toLowerCase()) {
                                            // User typed the same policy that's already fetched - do nothing
                                            e.preventDefault();
                                            setSearchInputValue('');
                                            return;
                                        }
                                        
                                        // Check if the typed value exists in current options
                                        const existingPolicyOptions = getPolicyNumberOptions();
                                        const existingOption = existingPolicyOptions.find(option => 
                                            option.value.toLowerCase() === searchInputValue.toLowerCase()
                                        );
                                        
                                        if (existingOption) {
                                            // Policy exists - apply local filter
                                            e.preventDefault();
                                            handleLocalPolicyFilter(existingOption);
                                            setSearchInputValue('');
                                        } else {
                                            // Policy doesn't exist - let CreatableSelect handle it or trigger search
                                            const isCreatingOption = document.querySelector('[class*="option"][class*="create"]');
                                            if (!isCreatingOption) {
                                                e.preventDefault();
                                                handlePolicyNumberSearch({ value: searchInputValue });
                                                setSearchInputValue('');
                                            }
                                        }
                                    }
                                }}
                                formatCreateLabel={(inputValue) => `Search for "${inputValue}"`}
                                noOptionsMessage={() => t('typeToSearch')}
                                variant="claims-filter"
                                isClearable={true}
                                toTranslate={false}
                                isCreatable={true}
                            />
                        </div>
                    )}
                    
                    {/* Policy Number Filter */}
                    {/* <div style={{ minWidth: '280px' }}>
                        <CustomSelect
                            options={getPolicyNumberOptions()}
                            placeholder={t('policyNo')}
                            value={policyNumberFilter}
                            onChange={setPolicyNumberFilter}
                            variant="claims-filter"
                            isClearable={true}
                            toTranslate={false}
                        />
                    </div> */}
                    {dashboardMode === 'invoices' && (
                        <div style={{ minWidth: '275px' }}>
                            <CustomSelect
                                options={getInvoiceNumberOptions()}
                                placeholder={t('invoiceNumber')}
                                value={invoiceNumberFilter}
                                onChange={setInvoiceNumberFilter}
                                variant="claims-filter"
                                isClearable={true}
                                toTranslate={false}
                            />
                        </div>
                    )}
                    {dashboardMode === 'policies' && (
                        <>
                            <div style={{ minWidth: '275px' }}>
                                <CustomSelect
                                    options={getCustomerNameOptions()}
                                    placeholder={t('name')}
                                    value={customerNameFilter}
                                    onChange={setCustomerNameFilter}
                                    variant="claims-filter"
                                    isClearable={true}
                                    toTranslate={false}
                                />
                            </div>
                            <div style={{ minWidth: '280px' }}>
                                <CustomSelect
                                    options={getStatusOptions()}
                                    placeholder={t('status')}
                                    value={statusFilter}
                                    onChange={setStatusFilter}
                                    variant="claims-filter"
                                    isClearable={true}
                                    toTranslate={false}
                                />
                            </div>
                        </>
                    )}
                </Box>
                {dashboardMode === 'invoices' && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                        {/* Charging needs no payment date — only a selection. The amounts
                            and every Stripe check happen in the dialog. */}
                        <button
                            className='button_red'
                            style={{ margin: 0 }}
                            disabled={!hasSelectedInvoices()}
                            onClick={onChargeSelected}
                        >
                            {t('charge')}
                        </button>
                        <button
                            className='button_red'
                            style={{ margin: 0 }}
                            disabled={!hasSelectedInvoices()}
                            onClick={handleAllocateClick}
                        >
                            {t('allocate')}
                        </button>
                    </div>
                )}
            </Box>

            {filteredData && filteredData.length > 0 ? (
                <div className="d-flex flex-column relative" style={{ width: '100%' }}>
                    <TableContainer
                        className="reset-table-spacing table-reset-header"
                        sx={{ margin: '0 0 -3px 0' }}
                        ref={headerRef}
                    >
                        <Table
                            className={`SlowShow reset-table-new table-${dashboardMode}`}
                            sx={{ minWidth: 600 }}
                        >
                            <TableHead>
                                <TableRow className={`reset-table-new__header ${dashboardMode === 'invoices' ? 'edit-rows' : ''}`}>
                                    {dashboardMode === 'invoices' &&
                                        <TableCell key={`header-empty`} className="cell-transparent">
                                            <div style={{ alignItems: 'flex-start' }}>
                                                <img
                                                    src={allSelected ? Checked : NotChecked}
                                                    alt="item-check-box"
                                                    onClick={() => selectAllHandler()}
                                                />
                                            </div>
                                        </TableCell>}
                                    {currentHeaders.map((header, index) => (
                                        <TableCell
                                            key={`header-${index}`}
                                            align={index > 0 ? 'inherit' : 'left'}
                                        >
                                            <div>
                                                <span>{t(header.title)}</span>
                                            </div>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                        </Table>
                    </TableContainer>

                    <TableContainer
                        className="reset-table-spacing"
                        sx={{ maxHeight: 550 }}
                        ref={bodyRef}
                        onScroll={syncScroll}
                    >
                        <Table
                            className={`SlowShow reset-table-new table-${dashboardMode}`}
                            sx={{ minWidth: 600 }}
                        >
                            <TableBody ref={listOfItemsRef}>
                                {filteredData.map((row, index) => {
                                    return dashboardMode === 'policies'
                                        ? renderPolicyRow(row, index)
                                        : renderInvoiceRow(row, index);
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Pagination Controls - Only show for policies mode */}
                    {dashboardMode === 'policies' && (
                        <Stack
                            spacing={2}
                            direction={'row'}
                            justifyContent={'flex-end'}
                            marginTop={2}
                        >
                            <div className="dark_red_title" style={{ fontSize: '24px', display: 'flex', alignItems: 'center' }}>
                                {t('showing')} {loadedPages.get(currentPage)?.startIndex} -
                                {loadedPages.get(currentPage)?.endIndex} {t('outOfTotal')}{' '}
                                {loadedPages.get(currentPage)?.totalPolicies}
                            </div>
                            <button
                                className="button_red"
                                disabled={disablePrevButton}
                                onClick={handlePrevPage}
                            >
                                {t('Back')}
                            </button>

                            <div className="dark_red_title" style={{ fontSize: '24px', display: 'flex', alignItems: 'center' }}>{currentPage}</div>

                            <button
                                className="button_red"
                                disabled={disableNextButton}
                                onClick={handleNextPage}
                            >
                                {t('Next')}
                            </button>
                        </Stack>
                    )}
                    {/* For invoices mode, show total count without pagination */}
                    {dashboardMode === 'invoices' && filteredData.length > 0 && (
                        <Stack
                            spacing={2}
                            direction={'row'}
                            justifyContent={'flex-end'}
                            marginTop={2}
                        >
                            <div className="dark_red_title" style={{ fontSize: '24px', display: 'flex', alignItems: 'center' }}>
                                {t('showing')} {filteredData.length} {t('invoices')}
                            </div>
                        </Stack>
                    )}
                </div>
            ) : (
                <div
                    className="d-flex flex-row justify-content-center align-items-center"
                    style={{ margin: '30px 0' }}
                >
                    <div
                        className="modal_container__body__info"
                        style={{ width: isMobileOnly ? 'unset' : '800px' }}
                    >
                        <span>
                            {dashboardMode === 'policies' ? t('noPoliciesFound') : t('noInvoicesFound')}
                        </span>
                        <img src={Exclamation} />
                    </div>
                </div>
            )}
        </>
    );
}

export default PaymentDashboard;